import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useZxing } from 'react-zxing';
import inventoryService from '../../services/inventoryService';
import { useStore } from '../../context/StoreContext';
import LabelPreview from '../../components/labels/LabelPreview';
import {
  getPreferredPrinterConfig,
  setPreferredPrinterConfig,
} from '../../utils/printerPreferences';
import labelService from '../../services/labelService';

const resolveProductPrice = (product) => {
  if (product?.price != null) return Number(product.price);
  if (product?.storePriceOverride?.price != null) return Number(product.storePriceOverride.price);
  if (product?.basePrice != null) return Number(product.basePrice);
  return 0;
};

const LABEL_TEMPLATE_ID = 'sample-label';
const PRINT_WRAPPER_STYLES = `
  body { margin: 0; padding: 20px; background: #fff; }
  .label-print-grid { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-start; justify-content: flex-start; text-align: center; }
  .label-print-item { page-break-inside: avoid; break-inside: avoid; display: block; }
`;

const parseTemplateDocument = (html) => {
  if (!html || typeof DOMParser === 'undefined') {
    return { head: '', body: '' };
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return {
      head: doc.head ? doc.head.innerHTML : '',
      body: doc.body ? doc.body.innerHTML : '',
    };
  } catch (err) {
    console.error('[HandheldLabels] Failed to parse label template', err);
    return { head: '', body: '' };
  }
};

const getPrinterPlugin = () => {
  if (!Capacitor.isPluginAvailable('ReceiptPrinter')) {
    return null;
  }
  return window?.Capacitor?.Plugins?.ReceiptPrinter || null;
};

const HandheldLabelsPage = () => {
  const { activeStore } = useStore();
  const searchInputRef = useRef(null);
  const previewRef = useRef(null);
  const lastCameraResultRef = useRef('');
  const [lookup, setLookup] = useState('');
  const [queue, setQueue] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [printerConfig, setPrinterConfig] = useState(() => {
    const cfg = getPreferredPrinterConfig('label');
    return {
      ...cfg,
      port: String(cfg.port || 9100),
    };
  });
  const [autoPrint, setAutoPrint] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem('labelAutoPrint') === 'true';
    } catch (err) {
      return false;
    }
  });
  const [isPrinting, setIsPrinting] = useState(false);
  const [labelTemplates, setLabelTemplates] = useState({});
  const [isCameraScannerOpen, setIsCameraScannerOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isCameraScannerOpen) {
      lastCameraResultRef.current = '';
    }
  }, [isCameraScannerOpen]);

  const ensureLabelTemplate = useCallback(
    async (product) => {
      if (!product?._id) return;
      const existing = labelTemplates[product._id];
      if (existing?.loading || existing?.body) {
        return;
      }
      setLabelTemplates((prev) => ({
        ...prev,
        [product._id]: { ...(prev[product._id] || {}), loading: true, error: null },
      }));
      try {
        const response = await labelService.renderLabel({
          templateId: LABEL_TEMPLATE_ID,
          productId: product._id,
          data: {
            name: product.name,
            price: resolveProductPrice(product),
            unit: 'ea',
            barcodeNumber: product.barcode || product.sku,
          },
        });
        const parsed = parseTemplateDocument(response.html);
        setLabelTemplates((prev) => ({
          ...prev,
          [product._id]: {
            loading: false,
            error: null,
            rawHtml: response.html,
            head: parsed.head,
            body: parsed.body,
          },
        }));
      } catch (err) {
        console.error('[HandheldLabels] Unable to render label template', err);
        setLabelTemplates((prev) => ({
          ...prev,
          [product._id]: {
            ...(prev[product._id] || {}),
            loading: false,
            error: err?.response?.data?.message || 'Unable to render label template',
            rawHtml: '',
            head: '',
            body: '',
          },
        }));
      }
    },
    [labelTemplates]
  );

  const addProductToQueue = useCallback((product) => {
    if (!product) return;
    setQueue((prev) => {
      const existing = prev.find((item) => item.product._id === product._id);
      if (existing) {
        return prev.map((item) =>
          item.product._id === product._id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
    ensureLabelTemplate(product);
  }, [ensureLabelTemplate]);

  const handleLookup = useCallback(
    async (arg) => {
      const options = typeof arg === 'string' ? { query: arg, silent: true } : arg || {};
      const { query = lookup, silent = false } = options;
      if (!activeStore?._id) {
        if (!silent) setStatus('Select a store to fetch inventory.');
        return;
      }
      const trimmed = query.trim();
      if (!trimmed) {
        if (!silent) setStatus('Enter a product name, SKU, or barcode.');
        return;
      }
      try {
        const results = await inventoryService.searchProducts(activeStore._id, trimmed);
        const match =
          results.find(
            (p) =>
              p.barcode?.toLowerCase() === trimmed.toLowerCase() ||
              p.sku?.toLowerCase() === trimmed.toLowerCase()
          ) || results[0];
        if (!match) {
          if (!silent) setStatus('No match found. Try a different search.');
          return;
        }
        addProductToQueue(match);
        setLookup('');
        if (!silent) setStatus(`Added "${match.name}" to label queue.`);
      } catch (err) {
        console.error(err);
        if (!silent) setStatus('Unable to fetch product data.');
      }
    },
    [activeStore?._id, addProductToQueue, lookup]
  );

  const handleCameraScanResult = useCallback(
    (rawValue) => {
      const text = typeof rawValue === 'string' ? rawValue.trim() : '';
      if (!text || text === lastCameraResultRef.current) {
        return;
      }
      lastCameraResultRef.current = text;
      setLookup(text);
      setIsCameraScannerOpen(false);
      setCameraError('');
      handleLookup({ query: text, silent: false });
    },
    [handleLookup]
  );

  const { ref: cameraRef } = useZxing({
    paused: !isCameraScannerOpen,
    onDecodeResult(result) {
      if (!result) {
        return;
      }
      try {
        const text = typeof result.getText === 'function' ? result.getText() : '';
        handleCameraScanResult(text);
      } catch (err) {
        console.error('[HandheldLabels] Failed to decode camera barcode', err);
      }
    },
    onError(err) {
      if (!isCameraScannerOpen) {
        return;
      }
      console.error('[HandheldLabels] Camera scanner error', err);
      setCameraError('Unable to access camera. Please check permissions.');
    },
  });

  const handleRemoveFromQueue = useCallback((productId) => {
    setQueue((prev) => prev.filter((item) => item.product._id !== productId));
  }, []);

  const handleQuantityChange = useCallback((productId, delta) => {
    setQueue((prev) =>
      prev.map((item) => {
        if (item.product._id !== productId) return item;
        const nextQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: nextQty };
      })
    );
  }, []);

  const handleQuantityInput = useCallback((productId, value) => {
    setQueue((prev) =>
      prev.map((item) =>
        item.product._id === productId
          ? { ...item, quantity: Math.max(1, Number(value) || 1) }
          : item
      )
    );
  }, []);

  const buildPrintableHtml = useCallback(
    (itemsParam) => {
    const items = Array.isArray(itemsParam) ? itemsParam : queue;
    if (!items.length) {
      return null;
    }
    const allTemplatesReady = items.every((item) => !!labelTemplates[item.product._id]?.body);
    if (allTemplatesReady) {
      const docWithHead = items
        .map((item) => labelTemplates[item.product._id])
        .find((doc) => doc?.head);
      if (!docWithHead) {
        return null;
      }
      let combined = '';
      items.forEach((item) => {
        const doc = labelTemplates[item.product._id];
        const qty = Math.max(1, item.quantity);
        combined += Array.from({ length: qty })
          .map(() => doc.body)
          .join('');
      });
      if (!combined.trim()) {
        return null;
      }
      const headContent = `${docWithHead.head || ''}<style>${PRINT_WRAPPER_STYLES}</style>`;
      return `<!DOCTYPE html>
<html>
  <head>${headContent}</head>
  <body>
    <div id="receipt-to-print" class="label-print-grid">${combined}</div>
  </body>
</html>`;
    }
    if (!previewRef.current) {
      return null;
    }
    const fallbackMarkup = previewRef.current.innerHTML;
    if (!fallbackMarkup || !fallbackMarkup.trim()) {
      return null;
    }
    const fallbackCombined = items
      .map((item) =>
        Array.from({ length: Math.max(1, item.quantity) })
          .map(() => fallbackMarkup)
          .join('')
      )
      .join('');
    return `<!DOCTYPE html>
<html>
  <head><style>${PRINT_WRAPPER_STYLES}</style></head>
  <body>
    <div id="receipt-to-print" class="label-print-grid">${fallbackCombined}</div>
  </body>
</html>`;
  },
  [labelTemplates, previewRef, queue]);

  const tryNativePrint = useCallback(async (html, configOverride) => {
    const plugin = getPrinterPlugin();
    if (!plugin) return { usedNative: false, error: null };
    try {
      const payload = { html, jobType: 'label' };
      const config =
        configOverride ||
        getPreferredPrinterConfig('label');
      if (config.transport === 'network' && config.host) {
        payload.networkHost = config.host;
        const portNumber = Number(config.port) || 9100;
        payload.networkPort = portNumber;
      } else if (config.bluetoothName) {
        payload.bluetoothNameHint = config.bluetoothName;
      }
      await plugin.print(payload);
      return { usedNative: true, error: null };
    } catch (err) {
      console.error('[HandheldLabels] Native print failed', err);
      return { usedNative: false, error: err };
    }
  }, []);

  const openBrowserPrint = useCallback((html) => {
    const printWindow = window.open('', '_blank', 'width=600,height=800');
    if (!printWindow) {
      window.print();
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 200);
  }, []);

  const refreshPrinterConfig = useCallback(() => {
    const latest = getPreferredPrinterConfig('label');
    setPrinterConfig({
      ...latest,
      port: String(latest.port || 9100),
    });
  }, []);

  const templateIssues = useMemo(
    () =>
      queue
        .map((item) => {
          const issue = labelTemplates[item.product._id]?.error;
          return issue ? `${item.product.name}: ${issue}` : null;
        })
        .filter(Boolean),
    [labelTemplates, queue]
  );
  const isPreparingTemplates = useMemo(
    () =>
      queue.some(
        (item) =>
          labelTemplates[item.product._id]?.loading &&
          !labelTemplates[item.product._id]?.body &&
          !labelTemplates[item.product._id]?.error
      ),
    [labelTemplates, queue]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('labelAutoPrint', autoPrint ? 'true' : 'false');
    } catch (err) {
      // ignore
    }
  }, [autoPrint]);

  const printLabelBatch = useCallback(
    async (items) => {
      if (!items.length) {
        setStatus('Add at least one product to the queue.');
        return;
      }
      setIsPrinting(true);
      setError('');
      try {
        for (const item of items) {
          const html = buildPrintableHtml([item]);
          if (!html) {
            setStatus('Preview not ready. Try again.');
            return;
          }
          const { usedNative, error: nativeError } = await tryNativePrint(html, printerConfig);
          if (nativeError) {
            setError(nativeError.message || 'Printer not available.');
            return;
          }
          if (!usedNative) {
            openBrowserPrint(html);
          }
        }
        const totalLabels = items.reduce((sum, item) => sum + item.quantity, 0);
        setStatus(`Sent ${totalLabels} label${totalLabels > 1 ? 's' : ''} to printer.`);
        setQueue([]);
        searchInputRef.current?.focus();
      } catch (err) {
        console.error('[HandheldLabels] Bulk print failed', err);
        setError('Unable to send labels to printer.');
      } finally {
        setIsPrinting(false);
      }
    },
    [buildPrintableHtml, openBrowserPrint, printerConfig, tryNativePrint]
  );

  const handlePrintAll = useCallback(async () => {
    await printLabelBatch(queue);
  }, [printLabelBatch, queue]);

  useEffect(() => {
    if (!autoPrint || isPrinting || !queue.length) return;
    const allReady = queue.every((item) => !!labelTemplates[item.product._id]?.body);
    if (allReady) {
      printLabelBatch(queue);
    }
  }, [autoPrint, isPrinting, queue, labelTemplates, printLabelBatch]);

  const currencySymbol = activeStore?.currencySymbol || '$';
  const totalLabels = useMemo(
    () => queue.reduce((sum, item) => sum + item.quantity, 0),
    [queue]
  );

  return (
    <>
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Add product</label>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <input
              type="text"
              ref={searchInputRef}
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLookup();
                }
              }}
              placeholder="Scan barcode or type product name"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 pr-10"
            />
            {lookup ? (
              <button
                type="button"
                aria-label="Clear barcode text"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full border border-gray-300 bg-white text-xs font-semibold text-slate-600"
                onClick={() => {
                  setLookup('');
                  searchInputRef.current?.focus();
                }}
              >
                X
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => handleLookup()}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white"
          >
            Add
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Label printer</label>
            <div className="flex flex-wrap gap-4 text-sm mt-2">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="labelPrinterTransport"
                  value="bluetooth"
                  checked={printerConfig.transport === 'bluetooth'}
                  onChange={() =>
                    setPrinterConfig((prev) => ({ ...prev, transport: 'bluetooth' }))
                  }
                />
                Bluetooth
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="labelPrinterTransport"
                  value="network"
                  checked={printerConfig.transport === 'network'}
                  onChange={() =>
                    setPrinterConfig((prev) => ({ ...prev, transport: 'network' }))
                  }
                />
                Network (LAN)
              </label>
            </div>
            {printerConfig.transport === 'bluetooth' ? (
              <>
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={printerConfig.bluetoothName || ''}
                    onChange={(e) =>
                      setPrinterConfig((prev) => ({ ...prev, bluetoothName: e.target.value }))
                    }
                    placeholder="Bluetooth printer name"
                    className="flex-1 border border-gray-300 rounded-xl px-3 py-2"
                  />
                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl border border-gray-300 text-sm"
                    onClick={() => {
                      const next = {
                        ...printerConfig,
                        port: Number(printerConfig.port) || undefined,
                      };
                      setPreferredPrinterConfig('label', next);
                      refreshPrinterConfig();
                      setStatus('Saved label printer preference.');
                    }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl border border-gray-300 text-sm"
                    onClick={() => {
                      setPreferredPrinterConfig('label', { transport: 'bluetooth', bluetoothName: '' });
                      setPrinterConfig({ transport: 'bluetooth', bluetoothName: '', host: '', port: '' });
                      refreshPrinterConfig();
                      setStatus('Cleared label printer preference.');
                    }}
                  >
                    Clear
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Update this when pairing a new Bluetooth printer.
                </p>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                  <input
                    type="text"
                    value={printerConfig.host || ''}
                    onChange={(e) =>
                      setPrinterConfig((prev) => ({ ...prev, host: e.target.value }))
                    }
                    placeholder="Printer IP address"
                    className="sm:col-span-2 border border-gray-300 rounded-xl px-3 py-2"
                  />
                  <input
                    type="number"
                    min="1"
                    max="65535"
                    value={printerConfig.port || ''}
                    onChange={(e) =>
                      setPrinterConfig((prev) => ({ ...prev, port: e.target.value }))
                    }
                    placeholder="Port"
                    className="border border-gray-300 rounded-xl px-3 py-2"
                  />
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl border border-gray-300 text-sm"
                    onClick={() => {
                      setPreferredPrinterConfig('label', {
                        ...printerConfig,
                        port: Number(printerConfig.port) || undefined,
                      });
                      refreshPrinterConfig();
                      setStatus('Saved label printer preference.');
                    }}
                  >
                    Save
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Configure the printer&apos;s LAN IP (static DHCP recommended) and optional port
                  (default 9100).
                </p>
              </>
            )}
          </div>
          <div className="flex items-end justify-end gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={autoPrint}
                  onChange={(e) => setAutoPrint(e.target.checked)}
                />
                Auto print after adding
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-3 py-2 rounded-xl border border-gray-300 text-sm"
                  onClick={() => setQueue([])}
                  disabled={!queue.length}
                >
                  Clear queue
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-xl bg-green-600 text-white disabled:bg-gray-400"
                  onClick={handlePrintAll}
                  disabled={!queue.length || isPrinting}
                >
                  {isPrinting ? 'Printing…' : `Print ${totalLabels || ''} labels`}
                </button>
              </div>
            </div>
          </div>
        {status && <p className="text-sm text-slate-600">{status}</p>}
        {isPreparingTemplates && (
          <p className="text-xs text-slate-500 mt-1">Preparing label templates…</p>
        )}
        {templateIssues.length > 0 && (
          <div className="mt-1 text-xs text-red-600 space-y-1">
            {templateIssues.map((msg, idx) => (
              <p key={`${msg}-${idx}`}>{msg}</p>
            ))}
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="bg-white rounded-2xl shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-semibold text-slate-900">Label queue</p>
            <p className="text-sm text-slate-500">
              {queue.length ? `${queue.length} product${queue.length > 1 ? 's' : ''} queued` : 'Scan products to add them here.'}
            </p>
          </div>
          <span className="text-sm font-semibold text-slate-600">
            {totalLabels} label{totalLabels === 1 ? '' : 's'}
          </span>
        </div>
        {queue.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing in the queue yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Barcode</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Price</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {queue.map((item) => (
                  <tr key={item.product._id}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">{item.product.name}</p>
                      <p className="text-xs text-slate-500">{item.product.sku}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">{item.product.barcode || item.product.sku}</td>
                    <td className="px-4 py-3 text-right text-sm text-slate-900">
                      {currencySymbol}
                      {resolveProductPrice(item.product).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          className="px-2 py-1 border border-gray-300 rounded-full"
                          onClick={() => handleQuantityChange(item.product._id, -1)}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleQuantityInput(item.product._id, e.target.value)}
                          className="w-16 text-center border border-gray-300 rounded-lg px-2 py-1"
                        />
                        <button
                          type="button"
                          className="px-2 py-1 border border-gray-300 rounded-full"
                          onClick={() => handleQuantityChange(item.product._id, 1)}
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="text-sm text-red-600"
                        onClick={() => handleRemoveFromQueue(item.product._id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    {isCameraScannerOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={() => setIsCameraScannerOpen(false)} />
        <div className="relative bg-black rounded-3xl w-[90%] max-w-md aspect-[3/4] overflow-hidden border border-white/30 shadow-2xl">
          <video ref={cameraRef} className="w-full h-full object-cover" muted playsInline />
          <div className="absolute inset-x-0 top-0 flex items-center justify-between text-white text-sm px-4 py-3 bg-gradient-to-b from-black/70 to-transparent">
            <span>Align barcode within the frame</span>
            <button
              type="button"
              className="px-3 py-1 rounded-full bg-white/20 hover:bg-white/40 text-white text-xs"
              onClick={() => setIsCameraScannerOpen(false)}
            >
              Close
            </button>
          </div>
          <div className="absolute inset-0 m-8 border-2 border-white/60 rounded-xl pointer-events-none" />
          {cameraError && (
            <p className="absolute bottom-0 inset-x-0 text-center text-xs text-red-300 bg-black/60 py-2 px-4">
              {cameraError}
            </p>
          )}
        </div>
      </div>
    )}
    <div
      style={{ position: 'absolute', left: '-10000px', top: 0 }}
      aria-hidden="true"
    >
      <div ref={previewRef} className="label-print-grid">
        {queue.flatMap((item) =>
          Array.from({ length: item.quantity }).map((_, idx) => (
            <LabelPreview
              key={`${item.product._id}-${idx}`}
              name={item.product.name}
              barcode={item.product.barcode || item.product.sku}
              price={resolveProductPrice(item.product)}
              currencySymbol={currencySymbol}
              unit="ea"
            />
          ))
        )}
      </div>
    </div>
    </>
  );
};

export default HandheldLabelsPage;
